import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper } from "@loanbossinc/component-library";
import { defaultField, defaultPercentField } from "helpers/row-logic-helper";

import {
  shouldShowCap
  , shouldShowCreditCharge
  , shouldShowEarlyTerminationDate
  , shouldShowEffectiveDate
  , shouldShowFloor
  , shouldShowHedgeProvider
  , shouldShowLowerStrike
  , shouldShowMarketCost
  , shouldShowMarketRate
  , shouldShowTerm
  , shouldShowMarkToMarket
  , shouldShowMaturityType
  , shouldShowNotional
  , shouldShowNotionalAtTermination
  , shouldShowPremium
  , shouldShowRate
  , shouldShowSpread
  , shouldShowStrike
  , shouldShowTerminationDate
  , shouldShowTerminationValue
  , shouldShowTradeDate
  , shouldShowUpperStrike
} from "./section-hedge-financials-rows-logic";

const DEFAULT_WIDTH = 138;

export const rows = {
  hedgeType: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Hedge Type",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING,
        map: data => defaultField(data, "hedgeType.value"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  hedgeStatus: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Hedge Status",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING,
        map: data => defaultField(data, "hedgeStatus.value"),
        Cell: tableSideColumnMapper
      }
    ]
  },
  hedgeTerm: {
    isVisible: data => shouldShowTerm(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Term",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING,
        map: data => defaultField(data, "term"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  marketRate: {
    isVisible: data => shouldShowMarketRate(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Market Rate",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "marketRate", 100),
        Cell: tableSideColumnMapper,
        decimals: 2
      }
    ]
  },

  marketCost: {
    isVisible: data => shouldShowMarketCost(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Market Cost",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "marketCost"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  markToMarket: {
    isVisible: data => shouldShowMarkToMarket(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Mark-to-Market",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "markToMarket"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  notional: {
    isVisible: data => shouldShowNotional(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Notional",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "notional"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  rate: {
    isVisible: data => shouldShowRate(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Rate",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "rate", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },

  strike: {
    isVisible: data => shouldShowStrike(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Strike",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "strike", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },

  upperStrike: {
    isVisible: data => shouldShowUpperStrike(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Upper Strike",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "upperStrike", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },

  lowerStrike: {
    isVisible: data => shouldShowLowerStrike(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Lower Strike",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "lowerStrike", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },

  cap: {
    isVisible: data => shouldShowCap(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Cap",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "cap", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },

  floor: {
    isVisible: data => shouldShowFloor(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Floor",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "floor", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },

  terminationValue: {
    isVisible: data => shouldShowTerminationValue(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Termination Value/Cost",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "terminationValue"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  notionalAtTermination: {
    isVisible: data => shouldShowNotionalAtTermination(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Notional at Termination",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "notionalAtTermination"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  index: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Index",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING,
        map: data => defaultField(data, "floatingIndex.name"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  tradeDate: {
    isVisible: data => shouldShowTradeDate(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Trade Date",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE,
        map: data => defaultField(data, "tradeDate"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  effectiveDate: {
    isVisible: data => shouldShowEffectiveDate(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Effective Date",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE,
        map: data => defaultField(data, "effectiveDate"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  terminationDate: {
    isVisible: data => shouldShowTerminationDate(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Termination Date",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE,
        map: data => defaultField(data, "terminationDate"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  earlyTerminationDate: {
    isVisible: data => shouldShowEarlyTerminationDate(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Early Termination Date",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE,
        map: data => defaultField(data, "earlyTerminationDate"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  maturityType: {
    isVisible: data => shouldShowMaturityType(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Maturity Type",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING,
        map: data => defaultField(data, "maturityType.name"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  premium: {
    isVisible: data => shouldShowPremium(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Premium",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "premium"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  hedgeProvider: {
    isVisible: data => shouldShowHedgeProvider(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Hedge Provider",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING,
        map: data => defaultField(data, "hedgeProvider.name"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  spread: {
    isVisible: data => shouldShowSpread(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Spread",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "spread", 100),
        Cell: tableSideColumnMapper,
        decimals: 2
      }
    ]
  },

  creditCharge: {
    isVisible: data => shouldShowCreditCharge(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Credit Charge (bps)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "creditCharge"),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  }
};
